import React from "react";
import PropTypes from "prop-types";
import { HiEye } from "react-icons/hi2";
import CustomBadge from "../../../ui/CustomBadge/CustomBadge";
import CustomSkeletonTable from "../../../ui/CustomSkeletonTable/CustomSkeletonTable";
import BaseTable from "../../../ui/CustomTanStackTable/CustomTanStackTable";
import useColumnVisibilityStore, {
  selectVisibleColumns,
} from "../../../../stores/columnVisibilityStore";
import { BULK_UPLOAD_STATUS_MAP } from "../../constants/bulkUploadsConstants";
import {
  formatKES,
  getDisplayValue,
  formatDateTimeToLocale,
} from "../../../../utils/transactionutils";


const OPTIONAL_COLUMN_KEYS = ["batchId", "amount", "charges", "total", "createdDateTime"];

function buildColumns({ onViewTransaction, hideActions }) {
  return [
    {
      accessorKey: "transactionId",
      header: "Transaction ID",
      cell: (info) => (
        <span className="font-medium text-gray-800">
          {getDisplayValue(info.getValue())}
        </span>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "batchId",
      header: "Batch Entry Ref",
      cell: (info) => (
        <span className="font-medium text-gray-800">
          {getDisplayValue(info.getValue())}
        </span>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "recipientName",
      header: "Recipient",
      cell: (info) => getDisplayValue(info.getValue()),
      enableSorting: false,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => {
        const status = getDisplayValue(info.getValue());
        return (
          <CustomBadge
            color={BULK_UPLOAD_STATUS_MAP[status] ?? "gray"}
            variant="light"
            size="sm"
          >
            {status}
          </CustomBadge>
        );
      },
      size: 120,
      minSize: 100,
      enableSorting: false,
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: (info) => formatKES(info.getValue()),
      enableSorting: false,
    },
    {
      accessorKey: "charges",
      header: "Charges",
      cell: (info) => formatKES(info.getValue()),
      enableSorting: false,
    },
    {
      id: "total",
      header: "Total",
      cell: (info) =>
        formatKES((info.row.original.amount ?? 0) + (info.row.original.charges ?? 0)),
      enableSorting: false,
    },
    {
      accessorKey: "createdDateTime",
      header: "Date",
      cell: (info) => formatDateTimeToLocale(info.getValue()),
      enableSorting: false,
    },
    ...(hideActions
      ? []
      : [
          {
            id: "actions",
            header: "Actions",
            cell: (info) => (
              <button
                className="p-1.5 rounded-md text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewTransaction?.(info.row.original);
                }}
                title="View transaction"
              >
                <HiEye size={16} />
              </button>
            ),
            enableSorting: false,
            size: 60,
          },
        ]),
  ];
}

function BatchTransactionsTable({
  data = [],
  isLoading = false,
  isFetching = false,
  pagination,
  totalPages = 0,
  totalElements = 0,
  onPaginationChange,
  onViewTransaction,
  hideActions = false,
}) {
  const visibleOptionalKeys = useColumnVisibilityStore(
    selectVisibleColumns("batchTransactions"),
  );

  const columns = React.useMemo(
    () => buildColumns({ onViewTransaction, hideActions }),
    [onViewTransaction, hideActions],
  );

  const columnVisibility = React.useMemo(() => {
    const visibility = {};
    OPTIONAL_COLUMN_KEYS.forEach((key) => {
      visibility[key] = visibleOptionalKeys.includes(key);
    });
    return visibility;
  }, [visibleOptionalKeys]);

  if (isLoading && data.length === 0) {
    return (
      <CustomSkeletonTable
        columns={[1, 2, 3]}
        rowCount={pagination?.pageSize ?? 10}
      />
    );
  }

  return (
    <BaseTable
      data={data}
      columns={columns}
      columnVisibility={columnVisibility}
      pageSize={pagination?.pageSize}
      totalPages={totalPages}
      totalElements={totalElements}
      pagination={pagination}
      isLoading={isLoading}
      isFetching={isFetching}
      onPaginationChange={onPaginationChange}
      paginationMode="server"
      emptyMessage="No transactions found"
      rowClassName={() => "bg-white-50 min-h-[50px]"}
    />
  );
}

BatchTransactionsTable.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  isFetching: PropTypes.bool,
  pagination: PropTypes.shape({
    pageIndex: PropTypes.number,
    pageSize: PropTypes.number,
  }),
  totalPages: PropTypes.number,
  totalElements: PropTypes.number,
  onPaginationChange: PropTypes.func,
  onViewTransaction: PropTypes.func,
  hideActions: PropTypes.bool,
};

export default BatchTransactionsTable;
